import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Plus, GripVertical, Trash2, Pencil } from 'lucide-react';
import { SortableList } from './SortableList';
import { Dropzone } from './Dropzone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) =>
  !src ? '' : src.startsWith('http') ? src : `${BACKEND_URL}${src}`;

/**
 * Shared About-subsection manager (used by History Stages, Committee, Past Presidents).
 * `variant` controls which fields are shown per row.
 *
 * Props:
 *  - field: MongoDB key inside about_modal (e.g. "history_phases", "committee_members")
 *  - title: header text
 *  - variant: 'history' | 'people'
 */
const AboutSubsectionManager = ({ field, title, variant = 'people' }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchAll = async () => {
    const res = await adminApi.get('/about-modal');
    setItems(Array.isArray(res.data?.[field]) ? res.data[field] : []);
  };
  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const persist = async (newList) => {
    await adminApi.patch(`/about-modal/${field}`, { value: newList });
  };

  const blank = () =>
    variant === 'history'
      ? { id: `new-${Date.now()}`, title: '', period: '', text: '', image: '', label_pos: 'top-left', image_fit: 'cover' }
      : { id: `new-${Date.now()}`, name: '', role: '', image: '', image_size: 'cover', image_pos: 'center' };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const idx = items.findIndex((x) => x.id === editing.id);
      const next = [...items];
      if (idx >= 0) next[idx] = editing;
      else next.push(editing);
      await persist(next);
      setItems(next);
      setEditing(null);
      toast({ title: 'Saved' });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const item = items.find((x) => x.id === id);
    const label = item?.title || item?.name || 'this entry';
    if (!confirm(`Are you sure you want to delete "${label}"? This cannot be undone.`)) return;
    const next = items.filter((x) => x.id !== id);
    await persist(next);
    setItems(next);
    toast({ title: 'Deleted' });
  };

  const handleReorder = async (reordered) => {
    setItems(reordered);
    await persist(reordered);
  };

  return (
    <div className="space-y-6" data-testid={`about-mgr-${field}`}>
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="text-xs text-gray-500">Add, edit, remove or reorder entries. Changes go live immediately.</p>
        </div>
        <Button onClick={() => setEditing(blank())} className="bg-[#1565C0]" data-testid={`add-${field}-btn`}>
          <Plus className="w-4 h-4 mr-2" /> Add
        </Button>
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {items.find((x) => x.id === editing.id) ? 'Edit' : 'New'} — {title}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              {variant === 'history' ? (
                <>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Stage Title</Label>
                      <Input
                        value={editing.title || ''}
                        onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                        placeholder="e.g. Foundation & Early Focus (1979)"
                        required
                        data-testid="stage-title-input"
                      />
                    </div>
                    <div>
                      <Label>Period / Stage Label</Label>
                      <Input
                        value={editing.period || ''}
                        onChange={(e) => setEditing({ ...editing, period: e.target.value })}
                        placeholder="e.g. Stage 1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Text (HTML allowed)</Label>
                    <Textarea
                      value={editing.text || ''}
                      onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                      rows={6}
                      data-testid="stage-text-input"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Image Label Position</Label>
                      <Select
                        value={editing.label_pos || 'top-left'}
                        onValueChange={(v) => setEditing({ ...editing, label_pos: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="top-left">Top Left</SelectItem>
                          <SelectItem value="top-right">Top Right</SelectItem>
                          <SelectItem value="bottom-left">Bottom Left</SelectItem>
                          <SelectItem value="bottom-right">Bottom Right</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Image Fit</Label>
                      <Select
                        value={editing.image_fit || 'cover'}
                        onValueChange={(v) => setEditing({ ...editing, image_fit: v })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cover">Cover (fills tile · may crop)</SelectItem>
                          <SelectItem value="contain">Contain (fully visible · no crop)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-gray-500 mt-1">Use "Contain" for collages / infographics so nothing gets cropped.</p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editing.name || ''}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                        required
                        data-testid="person-name-input"
                      />
                    </div>
                    <div>
                      <Label>Role / Title</Label>
                      <Input
                        value={editing.role || ''}
                        onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                        placeholder="e.g. President / 1979–1981"
                        data-testid="person-role-input"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Shared image uploader */}
              <div>
                <Label>Image</Label>
                {editing.image && (
                  <div className="mb-2 relative inline-block">
                    <img
                      src={resolveImg(editing.image)}
                      alt="preview"
                      className="w-40 h-40 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-7 w-7 p-0 rounded-full"
                      onClick={() => setEditing({ ...editing, image: '' })}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                <Dropzone
                  compact
                  onUpload={(url) => setEditing({ ...editing, image: url })}
                  label="Drop image (PNG or JPG)"
                />
                <Input
                  className="mt-2"
                  value={editing.image || ''}
                  onChange={(e) => setEditing({ ...editing, image: e.target.value })}
                  placeholder="Or paste a URL / /api/uploads/... path"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="bg-[#1565C0]" data-testid={`save-${field}-btn`}>
                  {loading ? 'Saving…' : 'Save'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <SortableList
        items={items}
        onReorder={handleReorder}
        renderItem={(p) => (
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <GripVertical className="w-5 h-5 text-gray-400 cursor-move flex-shrink-0" />
              <div className="w-16 h-16 bg-gray-100 border rounded overflow-hidden flex-shrink-0 flex items-center justify-center">
                {p.image ? (
                  <img src={resolveImg(p.image)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[10px] text-gray-400">No img</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-sm truncate">
                  {p.title || p.name}
                </h3>
                <p className="text-xs text-gray-500 truncate">{p.period || p.role || ''}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(p)}
                  title="Edit"
                  data-testid={`edit-${field}-${p.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDelete(p.id)}
                  title="Delete"
                  className="text-red-600 hover:text-red-700"
                  data-testid={`delete-${field}-${p.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      />
      {items.length === 0 && !editing && (
        <p className="text-center text-gray-500 py-8">No entries yet. Click "Add" to create one.</p>
      )}
    </div>
  );
};

export default AboutSubsectionManager;
